import React, { memo, useCallback } from "react";

import Slider from "antd/es/slider";
import cn from "classnames";
import numeral from "numeral";
import { useIntl } from "react-intl";

import { NumberField } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";

import styles from "./DistanceSelector.module.scss";

const distanceValueFormatter = (value: number | string | undefined) => {
  // let user enter a decimal separator, don't wipe it with numeral's replace
  // it is always a dot even if current locale uses another decimal separator!
  if (typeof value === "string" && value.endsWith(".")) {
    return value;
  } else {
    // due to a bug in InputNumber, it provides a value with a "." decimal separator on blur.
    // numeral may fails to parse it when numeral's decimal separator is set to ","
    // that's why we have to use standard js parseFloat to parse such string value
    const parsedValue = typeof value === "string" ? parseFloat(value) : value;
    return parsedValue === undefined ? "" : numeral(parsedValue).format("0.[00]");
  }
};

interface Props {
  className?: string;
  distance: number;
  label?: string;
  onChange: (distance: number) => void;
  onChangeInput?: (distance: number) => void;
  onChangeSlider?: (distance: number) => void;
}

const DistanceSelector: React.FC<Props> = ({
  className,
  distance,
  label,
  onChange,
  onChangeInput,
  onChangeSlider,
}) => {
  const intl = useIntl();

  const handleNumberFieldChange = useCallback(
    (value: number | undefined) => {
      if (value !== undefined) {
        const newValue = value > 0 ? value : 0.25;
        onChange(newValue);
        if (onChangeInput) {
          onChangeInput(newValue);
        }
      }
    },
    [onChange, onChangeInput]
  );

  const handleChangeSlider = useCallback(
    (value: number) => {
      onChange(value);
      if (onChangeSlider) {
        onChangeSlider(value);
      }
    },
    [onChange, onChangeSlider]
  );

  return (
    <div className={cn(styles.container, className)}>
      {!label && (
        <div className={styles.prefix}>
          {intl.formatMessage({
            id: "filters.location.distance.label",
            defaultMessage: "Is within",
            description: "Prefix in location filter's distance section",
          })}
        </div>
      )}
      <div className={styles.distance}>
        {label ?? undefined}
        <Slider
          className={styles.slider}
          max={1000}
          min={0.25}
          onChange={handleChangeSlider}
          step={0.25}
          tipFormatter={distanceValueFormatter}
          value={distance}
        />
        <NumberField
          className={styles.numberField}
          formatter={distanceValueFormatter}
          max={1000}
          min={0.25}
          onChange={handleNumberFieldChange}
          step={0.25}
          value={distance}
        />
        <span className={styles.suffix}>
          {localSettings.doesUseSiUnits()
            ? intl.formatMessage(
                {
                  id: "filters.location.distance.suffix.km",
                  defaultMessage: "km",
                  description: "Kilometer unit name in location filter",
                },
                { distance }
              )
            : intl.formatMessage(
                {
                  id: "filters.location.distance.suffix.mile",
                  defaultMessage: "{distance, plural, =0 {miles} one {mile} other {miles}}",
                  description: "Suffix in location filter",
                },
                { distance }
              )}
        </span>
      </div>
    </div>
  );
};

export default memo(DistanceSelector);
